<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();
useHead({
  titleTemplate: () => t("seo.layouts./viber-bot-promotion.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-bot-promotion.description") },
    {
      property: "og:title", content: () => t("seo.layouts./viber-bot-promotion.title")
    },
    { property: "og:description", content: () => t("seo.layouts./viber-bot-promotion.description") },
    {
      property: "og:image",
      content: "/images/chatbot.png",
    },
    { property: "og:image:width", content: "2668" },
    { property: "og:image:height", content: "2000" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
});

const popularQuestions = ref([
  {
    question: t('botPromotionLanding.faq[0].question'),
    answer: t('botPromotionLanding.faq[0].answer'),
    isOpen: false
  },
  {
    question: t('botPromotionLanding.faq[1].question'),
    answer: t('botPromotionLanding.faq[1].answer'),
    isOpen: false
  },
  {
    question: t('botPromotionLanding.faq[2].question'),
    answer: t('botPromotionLanding.faq[2].answer'),
    isOpen: false
  },
  {
    question: t('botPromotionLanding.faq[3].question'),
    answer: t('botPromotionLanding.faq[3].answer'),
    isOpen: false
  },
])

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})
</script>

<template>
  <div>
    <!-- Hero Banner -->
    <div class="main-banner">
      <div class="main-banner-content !justify-center gap-8">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('botPromotionLanding.hero.title') }}
          </div>
          <div class="banner-description">
            {{ $t('botPromotionLanding.hero.subtitle') }}
          </div>
          <div class="banner-action">
            <NuxtLink to="viber://chat?service=26076" class="button filled whitespace-nowrap !text-sm" external>
              <p>{{ $t('botPromotionLanding.hero.cta') }}</p>
            </NuxtLink>
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg preload sizes="xs:100vw sm:100vw md:50vw lg:600px xl:800px xxl:1000px" src="/images/chatbot.png" :alt="$t('seo.layouts./viber-bot-promotion.title')" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" class="w-full h-auto object-contain" />
        </div>
      </div>
    </div>

    <!-- What is Explore Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('botPromotionLanding.whatIsExplore.title') }}</div>
        </div>
        <div class="section-content">
          <div class="explore-description">
            <div class="explore-icon">
              <span class="pi pi-search text-4xl"></span>
            </div>
            <p>{{ $t('botPromotionLanding.whatIsExplore.description') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Services Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('botPromotionLanding.services.title') }}</div>
          <p class="mt-4 text-gray-600">{{ $t('botPromotionLanding.services.subtitle') }}</p>
        </div>
        <div class="section-content">
          <div class="services-grid">
            <div class="service-card">
              <div class="service-icon">
                <span class="pi pi-briefcase text-3xl"></span>
              </div>
              <h3>{{ $t('botPromotionLanding.services.commercialModel.title') }}</h3>
              <p>{{ $t('botPromotionLanding.services.commercialModel.description') }}</p>
            </div>
            <div class="service-card">
              <div class="service-icon verification-icon">
                <span class="pi pi-verified text-3xl"></span>
              </div>
              <h3>{{ $t('botPromotionLanding.services.verification.title') }}</h3>
              <p>{{ $t('botPromotionLanding.services.verification.description') }}</p>
            </div>
            <div class="service-card">
              <div class="service-icon seo-icon">
                <span class="pi pi-search text-3xl"></span>
              </div>
              <h3>{{ $t('botPromotionLanding.services.seoOptimization.title') }}</h3>
              <p>{{ $t('botPromotionLanding.services.seoOptimization.description') }}</p>
            </div>
            <div class="service-card">
              <div class="service-icon moderation-icon">
                <span class="pi pi-shield text-3xl"></span>
              </div>
              <h3>{{ $t('botPromotionLanding.services.moderationSupport.title') }}</h3>
              <p>{{ $t('botPromotionLanding.services.moderationSupport.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section service-domain" id="pricing">
      <div class="container">
        <div class="service-price">
          <div class="price-label">{{ $t('global.price') }}</div>
        </div>
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('botPromotionLanding.pricing.title') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-cards">
            <!-- Setup Fee -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>{{ $t('botPromotionLanding.pricing.setup.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">400 EUR</span>
                  <span class="price-note">{{ $t('botPromotionLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('botPromotionLanding.pricing.setup.description') }}</p>
              </div>
            </div>

            <!-- Monthly Fee -->
            <div class="pricing-card featured">
              <div class="pricing-card-header">
                <h3>{{ $t('botPromotionLanding.pricing.monthly.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">100 EUR</span>
                  <span class="price-period">/ {{ $t('global.month') }}</span>
                  <span class="price-note">{{ $t('botPromotionLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('botPromotionLanding.pricing.monthly.description') }}</p>
                <p class="mt-2 text-sm text-gray-400">{{ $t('botPromotionLanding.pricing.monthly.startDate') }}</p>
              </div>
            </div>
          </div>

          <!-- Note -->
          <div class="traffic-info">
            <div class="traffic-info-content">
              <span class="pi pi-info-circle text-2xl text-blue-700"></span>
              <div>
                <p>{{ $t('botPromotionLanding.pricing.note') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Benefits Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('botPromotionLanding.benefits.title') }}</div>
        </div>
        <div class="section-content">
          <div class="benefits-grid">
            <div class="benefit-card-large">
              <div class="benefit-large-icon">
                <span class="pi pi-users text-4xl"></span>
              </div>
              <h3>{{ $t('botPromotionLanding.benefits.reach.title') }}</h3>
              <p>{{ $t('botPromotionLanding.benefits.reach.description') }}</p>
            </div>
            <div class="benefit-card-large">
              <div class="benefit-large-icon official-icon">
                <span class="pi pi-verified text-4xl"></span>
              </div>
              <h3>{{ $t('botPromotionLanding.benefits.officialStatus.title') }}</h3>
              <p>{{ $t('botPromotionLanding.benefits.officialStatus.description') }}</p>
            </div>
            <div class="benefit-card-large">
              <div class="benefit-large-icon traffic-icon">
                <span class="pi pi-chart-line text-4xl"></span>
              </div>
              <h3>{{ $t('botPromotionLanding.benefits.passiveTraffic.title') }}</h3>
              <p>{{ $t('botPromotionLanding.benefits.passiveTraffic.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section">
      <div class="container">
        <div class="cta-section">
          <h2>{{ $t('botPromotionLanding.hero.title') }}</h2>
          <p class="cta-description">{{ $t('botPromotionLanding.hero.subtitle') }}</p>
          <div class="cta-buttons">
            <NuxtLink to="viber://chat?service=26076" class="button filled whitespace-nowrap" external>
              <p>{{ $t('botPromotionLanding.hero.cta') }}</p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="page-section">
      <div class="container">
        <ClientOnly>
          <PopularQuestions :title="$t('botPromotionLanding.faq.title')" :questions="popularQuestions" />
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.banner-image-container {
  width: 100%;
  margin-top: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
}

@media (max-width: 900px) {
  .banner-image-container {
    margin-top: 50px;
  }
}

@media (max-width: 850px) {
  .main-banner-content {
    display: block;
  }
}

.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}

/* Explore Description */
.explore-description {
  max-width: 800px;
  margin: 2rem auto 0;
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.explore-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.explore-description p {
  font-size: 1.1rem;
  color: #4b5563;
  line-height: 1.7;
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.service-card {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.service-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.service-icon.verification-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.service-icon.seo-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.service-icon.moderation-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.service-card h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.service-card p {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Pricing Cards */
.pricing-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.pricing-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  transition: transform 0.2s, box-shadow 0.2s;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.pricing-card.featured {
  border-color: #7c3aed;
  background: linear-gradient(180deg, #faf5ff 0%, white 100%);
}

.pricing-card-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.pricing-card-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.pricing-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.price-period {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 400;
}

.price-note {
  font-size: 0.75rem;
  color: #9ca3af;
}

.pricing-card-body {
  text-align: center;
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Traffic Info */
.traffic-info {
  margin-top: 2rem;
  background: #eff6ff;
  border-radius: 12px;
  padding: 1.5rem;
}

.traffic-info-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.traffic-info p {
  font-size: 0.95rem;
  color: #6b7280;
}

/* Benefits Grid */
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.benefit-card-large {
  text-align: center;
  padding: 2.5rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.benefit-card-large:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.benefit-large-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.benefit-large-icon.official-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.benefit-large-icon.traffic-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.benefit-card-large h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
}

.benefit-card-large p {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.6;
}

/* CTA Section */
.cta-section {
  text-align: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  border-radius: 24px;
  color: white;
}

.cta-section h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.cta-description {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-buttons .button.filled {
  background: white;
  color: #7c3aed;
}
</style>
